import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  query,
  Timestamp,
  serverTimestamp,
  setDoc,
  where
} from 'firebase/firestore';
import { onAuthStateChanged, getAuth } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faReceipt,
} from '@fortawesome/free-solid-svg-icons';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db } from '../../firebase';
import { logAdminActivity } from '../../utils/activityLogger';
import {
  formatRequisitionCurrency,
  formatRequisitionDate,
  getRequisitionStatusTone,
  normalizeRequisitionStatus,
  toDateValue
} from './requisitionHelpers';
import {
  getMonthKey,
  getMonthLabel
} from '../../utils/financeWorkflow';

const actionOptions = ['Approved', 'Partially Approved', 'Review', 'Rejected', 'On Hold'];

const LOTUS_API_BASE_URL = process.env.REACT_APP_LOTUS_API_BASE_URL
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/lotusApi';
const LOTUS_RECONCILE_INTERVAL_MS = 15 * 60 * 1000;

const normalizeLotusTransactionId = (value) => String(value || '')
  .trim()
  .replace(/^TR[-_ ]*/i, '');

const normalizeLotusStatus = (value) => String(value || 'pending').trim().toLowerCase();

const isSuccessfulLotusStatus = (value) => {
  const normalized = normalizeLotusStatus(value);
  return ['paid', 'processed', 'completed', 'successful', 'success', 'succeeded', 'approved'].includes(normalized);
};

const isFailedLotusStatus = (value) => {
  const normalized = normalizeLotusStatus(value);
  return ['failed', 'failure', 'rejected', 'declined', 'cancelled', 'canceled', 'error'].includes(normalized);
};

const resolveRequisitionPaymentStatus = (requisition = {}, payment = {}) => {
  const candidates = [
    payment.paymentStatus,
    payment.status,
    payment.lotusTransactionStatus,
    requisition.paymentStatus,
    requisition.status,
    requisition.lotusTransactionStatus
  ]
    .map((value) => normalizeLotusStatus(value))
    .filter(Boolean);

  if (candidates.some((value) => isSuccessfulLotusStatus(value))) {
    return 'Paid';
  }

  if (candidates.some((value) => isFailedLotusStatus(value))) {
    return 'Failed';
  }

  if (candidates.some((value) => ['pending', 'processing', 'submitted'].includes(value)) || payment.lotusTransactionId || requisition.lotusTransactionId) {
    return 'Pending';
  }

  return '';
};

const getRequisitionPaymentTone = (paymentStatus) => {
  const normalized = normalizeLotusStatus(paymentStatus);

  if (['paid', 'processed', 'completed', 'successful', 'success', 'succeeded'].includes(normalized)) {
    return 'approved';
  }

  if (['failed', 'failure', 'rejected', 'declined', 'cancelled', 'canceled', 'error'].includes(normalized)) {
    return 'rejected';
  }

  return 'pending';
};

const findNestedValue = (source, keys) => {
  const queue = [source];
  const seen = new Set();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || seen.has(current)) {
      continue;
    }

    seen.add(current);

    for (const key of keys) {
      const value = current[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return value;
      }
    }

    for (const value of Object.values(current)) {
      if (value && typeof value === 'object') {
        queue.push(value);
      }
    }
  }

  return '';
};

const extractLotusPaymentDetails = (payload) => {
  const source = payload?.data?.transaction
    || payload?.data
    || payload?.transaction
    || payload?.payment
    || payload?.result
    || payload
    || {};

  return {
    lotusTransactionId: normalizeLotusTransactionId(findNestedValue(source, [
      'transaction_id',
      'transactionId',
      'payment_id',
      'paymentId',
      'id'
    ])),
    lotusTransactionStatus: String(findNestedValue(source, [
      'status',
      'payment_status',
      'paymentStatus',
      'state',
      'result',
      'transaction_status',
      'transactionStatus'
    ])).trim(),
    lotusTransactionMessage: String(findNestedValue(payload, [
      'message',
      'detail',
      'description',
      'response_message',
      'responseMessage'
    ])).trim()
  };
};

const normalizeAdminType = (value) => {
  const normalized = String(value || 'admin').trim().toLowerCase();
  if (normalized === 'superadmin') return 'superAdmin';
  if (normalized === 'viewer') return 'viewer';
  return 'admin';
};

const resolveApproverProfile = async (currentUser) => {
  if (!currentUser) {
    return {
      approverName: 'Finance',
      approverEmail: '',
      approverId: '',
      approverRole: 'admin'
    };
  }

  let approverName = currentUser.displayName || currentUser.email || 'Finance';
  let approverRole = 'admin';

  try {
    const byUserId = await getDocs(
      query(collection(db, 'Admins'), where('userID', '==', currentUser.uid))
    );

    if (!byUserId.empty) {
      const adminData = byUserId.docs[0].data();
      approverName = adminData.userName || adminData.userEmail || approverName;
      approverRole = normalizeAdminType(adminData.adminType);
    } else if (currentUser.email) {
      const byEmail = await getDocs(
        query(collection(db, 'Admins'), where('userEmail', '==', currentUser.email))
      );

      if (!byEmail.empty) {
        const adminData = byEmail.docs[0].data();
        approverName = adminData.userName || adminData.userEmail || approverName;
        approverRole = normalizeAdminType(adminData.adminType);
      }
    }
  } catch (error) {
    console.error('Failed to resolve approver profile:', error);
  }

  return {
    approverName,
    approverEmail: currentUser.email || '',
    approverId: currentUser.uid,
    approverRole
  };
};

function RequisitionApprovalsComp() {
  const auth = getAuth();
  const [requiLists, setRequiLists] = useState([]);
  const [branchMapping, setBranchMapping] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [approverProfile, setApproverProfile] = useState({
    approverName: 'Finance',
    approverEmail: '',
    approverId: '',
    approverRole: 'admin'
  });
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [requisitionPayments, setRequisitionPayments] = useState([]);
  const [actionModalShow, setActionModalShow] = useState(false);
  const [action, setAction] = useState('Approved');
  const [comment, setComment] = useState('');
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const fetchApproverProfile = async () => {
      const profile = await resolveApproverProfile(currentUser);
      setApproverProfile(profile);
    };

    fetchApproverProfile();
  }, [currentUser]);

  useEffect(() => {
    const fetchBranches = async () => {
      const branchSnapshot = await getDocs(collection(db, 'Branches'));
      const mapping = {};

      branchSnapshot.forEach((branchDoc) => {
        mapping[branchDoc.id] = branchDoc.data().branchName;
      });

      setBranchMapping(mapping);
    };

    fetchBranches();
  }, []);

  useEffect(() => {
    const fetchRequisitions = async () => {
      const [requisitionSnapshot, requisitionPaymentSnapshot] = await Promise.all([
        getDocs(collection(db, 'Requisitions')),
        getDocs(collection(db, 'RequisitionPayments'))
      ]);

      const paymentMap = requisitionPaymentSnapshot.docs.reduce((accumulator, itemDoc) => {
        const payment = { id: itemDoc.id, ...itemDoc.data() };
        const requisitionId = String(payment.requisitionId || '').trim();
        if (!requisitionId) {
          return accumulator;
        }

        const paymentStatus = normalizeLotusStatus(payment.paymentStatus || payment.status);
        if (!['paid', 'processed', 'completed', 'successful', 'success', 'succeeded'].includes(paymentStatus)) {
          return accumulator;
        }

        const nextTime = toDateValue(payment.paymentDate || payment.createdAt)?.getTime() || 0;
        const current = accumulator[requisitionId];
        const currentTime = current ? toDateValue(current.paymentDate || current.createdAt)?.getTime() || 0 : -1;

        if (!current || nextTime >= currentTime) {
          accumulator[requisitionId] = payment;
        }

        return accumulator;
      }, {});

      const nextRequisitions = requisitionSnapshot.docs
        .map((itemDoc) => {
          const itemData = itemDoc.data();
          const latestPayment = paymentMap[itemDoc.id] || {};
          const paymentStatus = resolveRequisitionPaymentStatus(itemData, latestPayment);

          return {
            id: itemDoc.id,
            ...itemData,
            status: normalizeRequisitionStatus(itemData.status),
            paymentStatus,
            lotusTransactionStatus: latestPayment.lotusTransactionStatus || itemData.lotusTransactionStatus || '',
            lotusPaymentTag: latestPayment.lotusPaymentTag || itemData.lotusPaymentTag || '',
            requisitionPaymentId: latestPayment.id || itemData.requisitionPaymentId || '',
            lotusTransactionId: latestPayment.lotusTransactionId || itemData.lotusTransactionId || ''
          };
        })
        .sort((a, b) => {
          const aDate = toDateValue(a.timeStamp)?.getTime() || 0;
          const bDate = toDateValue(b.timeStamp)?.getTime() || 0;
          return bDate - aDate;
        });

      setRequiLists(nextRequisitions);
      setRequisitionPayments(
        requisitionPaymentSnapshot.docs.map((itemDoc) => ({ id: itemDoc.id, ...itemDoc.data() }))
      );
    };

    fetchRequisitions();
  }, []);

  const summary = useMemo(() => {
    const counts = {
      pending: 0,
      approved: 0,
      hold: 0,
      rejected: 0
    };

    requiLists.forEach((item) => {
      const tone = getRequisitionStatusTone(item.status);

      if (tone === 'pending') {
        counts.pending += 1;
      } else if (tone === 'approved' || tone === 'partial') {
        counts.approved += 1;
      } else if (tone === 'hold') {
        counts.hold += 1;
      } else if (tone === 'rejected') {
        counts.rejected += 1;
      } else if (tone === 'review') {
        counts.hold += 1;
      }
    });

    return counts;
  }, [requiLists]);

  const filteredRequisitions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return requiLists.filter((item) => {
      const matchesStatus = statusFilter === 'All' || normalizeRequisitionStatus(item.status) === statusFilter;
      const matchesSearch =
        !normalizedSearch ||
        [
          item.itemTitle,
          item.itemDesc,
          item.requesterName,
          item.requesterEmail,
          branchMapping[item.selectedCampus]
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch));

      return matchesStatus && matchesSearch;
    });
  }, [branchMapping, requiLists, searchTerm, statusFilter]);

  const isSuperAdmin = approverProfile.approverRole === 'superAdmin';

  const openActionModal = (item) => {
    setSelectedRequisition(item);
    setAction(normalizeRequisitionStatus(item.status) === 'Pending Approval' ? 'Approved' : normalizeRequisitionStatus(item.status));
    setComment(item.approvalComment || item.comment || '');
    setActionModalShow(true);
  };

  const handleActionSubmit = async () => {
    if (!selectedRequisition) {
      return;
    }

    if (!action) {
      swal('Missing action', 'Please choose an approval action first.', 'warning');
      return;
    }

    const normalizedAction = normalizeRequisitionStatus(action);
    const requiresLotusPayment = normalizedAction === 'Approved';
    const paymentPhoneNumber = String(selectedRequisition.paymentPhoneNumber || '').trim();

    if (requiresLotusPayment && !paymentPhoneNumber) {
      swal('Missing phone number', 'Add the requisition MPESA number before approving this request.', 'warning');
      return;
    }

    if (requiresLotusPayment && !auth.currentUser) {
      swal('Session expired', 'Please sign in again before approving this requisition.', 'warning');
      return;
    }

    setIsSubmittingAction(true);

    try {
      const approvalDate = Timestamp.now();
      const branchName = branchMapping[selectedRequisition.selectedCampus] || 'Campus not set';
      const paymentTag = requiresLotusPayment
        ? `requisition-${selectedRequisition.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        : '';
      let lotusPaymentDetails = null;
      let lotusPaymentData = null;
      let lotusTransactionId = '';
      let lotusTransactionStatus = '';
      let lotusTransactionMessage = '';
      let paymentRef = null;
      let paymentRecord = null;
      let paymentPayloadForRequisition = {};

      if (requiresLotusPayment) {
        const currentUser = auth.currentUser;
        const idToken = await currentUser.getIdToken();
        const lotusPayload = {
          amount: Number(selectedRequisition.totalAmount || 0),
          payment_method: 'mpesa',
          recipient_name: selectedRequisition.requesterName || 'Staff',
          phone_number: paymentPhoneNumber,
          payment_purpose: `Requisition payment for ${selectedRequisition.itemTitle || 'request'} - ${branchName}`,
          payment_tag: paymentTag,
          payment_category: 'requisition'
        };

        const lotusResponse = await fetch(`${LOTUS_API_BASE_URL}/payments`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(lotusPayload)
        });

        const lotusResponseBody = await lotusResponse.json().catch(() => null);
        if (!lotusResponse.ok) {
          const lotusErrorMessage = lotusResponseBody?.error?.message
            || lotusResponseBody?.error?.detail
            || lotusResponseBody?.error
            || lotusResponseBody?.message
            || 'Lotus rejected the requisition payment request.';
          throw new Error(typeof lotusErrorMessage === 'string' ? lotusErrorMessage : 'Lotus rejected the requisition payment request.');
        }

        lotusPaymentDetails = extractLotusPaymentDetails(lotusResponseBody);
        lotusPaymentData = lotusResponseBody?.data?.transaction
          || lotusResponseBody?.data
          || lotusResponseBody
          || {};
        lotusTransactionId = lotusPaymentDetails.lotusTransactionId;
        lotusTransactionStatus = lotusPaymentDetails.lotusTransactionStatus || 'pending';
        lotusTransactionMessage = lotusPaymentDetails.lotusTransactionMessage
          || lotusResponseBody?.message
          || 'Payment submitted to Lotus.';

        paymentRef = doc(collection(db, 'RequisitionPayments'));
        const paymentTimestamp = approvalDate;
        const paymentPeriodKey = getMonthKey(paymentTimestamp.toDate());
        const paymentRecordData = {
          requisitionId: selectedRequisition.id,
          requisitionTitle: selectedRequisition.itemTitle || 'Requisition',
          requesterId: selectedRequisition.requesterId || '',
          requesterName: selectedRequisition.requesterName || '',
          branchId: selectedRequisition.selectedCampus || '',
          branchName,
          payee: selectedRequisition.requesterName || 'Staff',
          amount: Number(selectedRequisition.totalAmount || 0),
          paymentDate: paymentTimestamp,
          paymentMethod: 'MPESA',
          paymentPhoneNumber,
          paymentStatus: 'Pending',
          status: 'Pending',
          reference: '',
          notes: '',
          paymentPeriodKey,
          paymentPeriodLabel: getMonthLabel(paymentPeriodKey),
          expectedAmount: Number(selectedRequisition.totalAmount || 0),
          createdAt: paymentTimestamp,
          createdBy: currentUser.uid,
          docId: paymentRef.id,
          transactionGroupId: paymentTag,
          lotusPaymentTag: paymentTag,
          lotusPaymentMethod: 'mpesa',
          lotusTransactionId,
          lotusTransactionStatus,
          lotusTransactionMessage,
          lotusPaymentResponse: lotusPaymentData,
          paymentDestinationSummary: {
            method: 'MPESA',
            phoneLast4: paymentPhoneNumber.slice(-4)
          },
          sourcePage: 'requisition-approvals'
        };

        await setDoc(paymentRef, paymentRecordData);
        paymentRecord = { id: paymentRef.id, ...paymentRecordData };
        paymentPayloadForRequisition = {
          paymentStatus: 'Pending',
          paymentMethod: 'MPESA',
          paymentPhoneNumber,
          lotusPaymentTag: paymentTag,
          lotusPaymentMethod: 'mpesa',
          lotusTransactionId,
          lotusTransactionStatus,
          lotusTransactionMessage,
          lotusPaymentResponse: lotusPaymentData,
          requisitionPaymentId: paymentRef.id,
          paymentSubmittedAt: paymentTimestamp,
          paymentSubmittedById: currentUser.uid,
          paymentSubmittedByName: approverProfile.approverName,
          paymentSubmittedByEmail: approverProfile.approverEmail
        };

        setRequisitionPayments((prev) => [paymentRecord, ...prev]);
      }

      await setDoc(
        doc(db, 'Requisitions', selectedRequisition.id),
        {
          status: normalizedAction,
          approvalComment: comment.trim(),
          comment: comment.trim(),
          approvalDate: serverTimestamp(),
          approvedById: approverProfile.approverId,
          approvedByName: approverProfile.approverName,
          approvedByEmail: approverProfile.approverEmail,
          ...paymentPayloadForRequisition
        },
        { merge: true }
      );

      setRequiLists((prev) =>
        prev.map((item) =>
          item.id === selectedRequisition.id
            ? {
                ...item,
                status: normalizedAction,
                approvalComment: comment.trim(),
                comment: comment.trim(),
                approvalDate: new Date(),
                approvedById: approverProfile.approverId,
                approvedByName: approverProfile.approverName,
                approvedByEmail: approverProfile.approverEmail,
                ...paymentPayloadForRequisition
              }
            : item
        )
      );

      logAdminActivity({
        action: requiresLotusPayment ? 'Approved requisition and submitted Lotus payment' : 'Updated requisition approval',
        details: requiresLotusPayment
          ? `Approved ${selectedRequisition.itemTitle} for ${formatRequisitionCurrency(selectedRequisition.totalAmount)} and submitted it to Lotus.`
          : `${normalizedAction} requisition for ${selectedRequisition.itemTitle}.`,
        meta: {
          targetCollection: 'Requisitions',
          targetId: selectedRequisition.id,
          lotusTransactionId,
          lotusPaymentTag: paymentTag
        }
      });

      swal(
        'Success!',
        requiresLotusPayment
          ? 'Requisition approved and sent to Lotus for payment processing.'
          : 'Requisition status updated successfully.',
        'success'
      );
      setStatusFilter('All');
      setActionModalShow(false);
      setSelectedRequisition(null);
      setComment('');
    } catch (error) {
      console.error('Failed to update requisition status:', error);
      swal('Error!', error.message || 'The requisition could not be updated right now.', 'error');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const reconcilePendingPayments = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser || requisitionPayments.length === 0) {
        return;
      }

      const pendingPayments = requisitionPayments.filter((payment) => {
        const status = normalizeLotusStatus(payment.paymentStatus || payment.status);
        return Boolean(payment.lotusTransactionId) && ['pending', 'processing', 'submitted'].includes(status);
      });

      if (pendingPayments.length === 0) {
        return;
      }

      try {
        const idToken = await currentUser.getIdToken();
        const reconciledAt = Timestamp.now();

        await Promise.all(pendingPayments.map(async (payment) => {
          const response = await fetch(`${LOTUS_API_BASE_URL}/payments/${payment.lotusTransactionId}`, {
            headers: {
              Authorization: `Bearer ${idToken}`
            }
          });

          const payload = await response.json().catch(() => null);
          if (!response.ok) {
            return;
          }

          const lotusPaymentData = payload?.data?.transaction || payload?.data || payload || {};
          const lotusPaymentDetails = extractLotusPaymentDetails(payload);
          const lotusStatusRaw = lotusPaymentDetails.lotusTransactionStatus;
          const paymentStatus = isSuccessfulLotusStatus(lotusStatusRaw)
            ? 'Paid'
            : isFailedLotusStatus(lotusStatusRaw)
              ? 'Failed'
              : null;

          if (!paymentStatus) {
            return;
          }

          if (normalizeLotusStatus(payment.paymentStatus || payment.status) === normalizeLotusStatus(paymentStatus)) {
            return;
          }

          const paymentRef = doc(db, 'RequisitionPayments', payment.id);
          await setDoc(paymentRef, {
            paymentStatus,
            status: paymentStatus,
            lotusTransactionStatus: lotusStatusRaw || paymentStatus,
            lotusPaymentResponse: lotusPaymentData,
            lotusReconciledAt: reconciledAt,
            lotusReconciledSource: 'polling'
          }, { merge: true });

          const requisitionRef = doc(db, 'Requisitions', payment.requisitionId);
          await setDoc(requisitionRef, {
            paymentStatus,
            lotusTransactionStatus: lotusStatusRaw || paymentStatus,
            lotusPaymentResponse: lotusPaymentData,
            lotusReconciledAt: reconciledAt,
            lotusReconciledSource: 'polling'
          }, { merge: true });

          if (!cancelled) {
            setRequisitionPayments((currentPayments) => currentPayments.map((item) => (
              item.id === payment.id
                ? {
                    ...item,
                    paymentStatus,
                    status: paymentStatus,
                    lotusTransactionStatus: lotusStatusRaw || paymentStatus,
                    lotusPaymentResponse: lotusPaymentData,
                    lotusReconciledAt: reconciledAt,
                    lotusReconciledSource: 'polling'
                  }
                : item
            )));

            setRequiLists((currentRequisitions) => currentRequisitions.map((item) => (
              item.id === payment.requisitionId
                ? {
                    ...item,
                    paymentStatus,
                    lotusTransactionStatus: lotusStatusRaw || paymentStatus,
                    lotusPaymentResponse: lotusPaymentData,
                    lotusReconciledAt: reconciledAt,
                    lotusReconciledSource: 'polling'
                  }
                : item
            )));
          }
        }));
      } catch (error) {
        console.error('Failed to reconcile requisition payments:', error);
      }
    };

    reconcilePendingPayments();
    const intervalId = window.setInterval(reconcilePendingPayments, LOTUS_RECONCILE_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [auth, requisitionPayments]);

  return (
    <div className="teamsComponent requisitionPage">
      {!isSuperAdmin ? (
        <div className="requisitionEmptyState">
          <h6>Access restricted</h6>
          <p>Requisition approvals are only available to super admins.</p>
        </div>
      ) : (
        <>
      <div className="teamsTopBar requisitionTopBar">
        <div>
          <h5 className="tHide1">Requisition Approvals</h5>
          <h5 className="tHide2">Requisition Approvals</h5>
          <p className="requisitionIntro">
            Review office requests, leave notes for the team, and decide what finance can approve.
          </p>
          {!isSuperAdmin ? (
            <p className="requisitionAccessNote">
              Approval actions are restricted to users whose admin type is set to <strong>superAdmin</strong>.
            </p>
          ) : null}
        </div>
      </div>

      <div className="requisitionSummaryGrid">
        <article className="requisitionSummaryCard">
          <span>Pending approval</span>
          <strong>{summary.pending}</strong>
        </article>
        <article className="requisitionSummaryCard">
          <span>Approved / partial</span>
          <strong>{summary.approved}</strong>
        </article>
        <article className="requisitionSummaryCard">
          <span>On hold</span>
          <strong>{summary.hold}</strong>
        </article>
        <article className="requisitionSummaryCard">
          <span>Rejected</span>
          <strong>{summary.rejected}</strong>
        </article>
      </div>

      <div className="requisitionToolbar">
        <div className="requisitionFilterGroup">
          <label htmlFor="requisition-approval-search">Search</label>
          <input
            id="requisition-approval-search"
            type="text"
            placeholder="Item, requester, branch..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <div className="requisitionFilterGroup">
          <label htmlFor="requisition-approval-status-filter">Status</label>
          <select
            id="requisition-approval-status-filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="All">All statuses</option>
            <option value="Pending Approval">Pending approval</option>
            <option value="Review">Review</option>
            <option value="Approved">Approved</option>
            <option value="Partially Approved">Partially approved</option>
            <option value="On Hold">On hold</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
      </div>

      {filteredRequisitions.length > 0 ? (
        <div className="requisitionTableWrap">
          <table className="requisitionTable">
            <thead>
              <tr>
                <th>Item</th>
                <th>Branch</th>
                <th>Requester</th>
                <th>Needed by</th>
                <th>Amount</th>
                <th>Approval status</th>
                <th>Payment status</th>
                <th>Finance note</th>
                <th>Receipt</th>
                <th>Review</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequisitions.map((item) => {
                const tone = getRequisitionStatusTone(item.status);
                const normalizedStatus = normalizeRequisitionStatus(item.status);
                const paymentTone = getRequisitionPaymentTone(item.paymentStatus);
                const paymentStatus = item.paymentStatus || (normalizedStatus === 'Approved' ? 'Pending' : 'Not initiated');

                return (
                  <tr key={item.id}>
                    <td>
                      <div className="requisitionTableMainCell">
                        <strong>{item.itemTitle}</strong>
                        <span>{item.itemDesc}</span>
                      </div>
                    </td>
                    <td>{branchMapping[item.selectedCampus] || 'Campus not set'}</td>
                    <td>
                      <div className="requisitionTableSecondaryCell">
                        <strong>{item.requesterName || 'Staff member'}</strong>
                        <span>{formatRequisitionDate(item.timeStamp)}</span>
                      </div>
                    </td>
                    <td>{formatRequisitionDate(item.itemsNeededBy)}</td>
                    <td>
                      <div className="requisitionTableSecondaryCell">
                        <strong>{formatRequisitionCurrency(item.totalAmount)}</strong>
                        <span>
                          {item.numberOfUnits} unit{Number(item.numberOfUnits) === 1 ? '' : 's'} at {formatRequisitionCurrency(item.amountPerUnit)} each
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`requisitionStatusPill requisitionStatusPill--${tone}`}>
                        {normalizedStatus}
                      </span>
                    </td>
                    <td>
                      <span className={`requisitionStatusPill requisitionStatusPill--${paymentTone}`}>
                        {paymentStatus}
                      </span>
                    </td>
                    <td>
                      <div className="requisitionTableSecondaryCell">
                        <strong>{item.approvedByName || item.approvedByEmail || 'Not reviewed yet'}</strong>
                        <span>{item.approvalComment || 'No finance note yet'}</span>
                      </div>
                    </td>
                    <td>
                      {item.receiptUrl ? (
                        <a
                          className="requisitionGhostBtn requisitionGhostBtn--table"
                          href={item.receiptUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <FontAwesomeIcon icon={faReceipt} /> Receipt
                        </a>
                      ) : (
                        <span className="requisitionTableMuted">No receipt</span>
                      )}
                    </td>
                    <td>
                      {normalizedStatus !== 'Approved' && isSuperAdmin ? (
                        <button
                          type="button"
                          className="requisitionQuickBtn requisitionQuickBtn--approve requisitionQuickBtn--table"
                          onClick={() => openActionModal(item)}
                        >
                          <FontAwesomeIcon icon={faCheckCircle} /> Review
                        </button>
                      ) : !isSuperAdmin ? (
                        <span className="requisitionTableMuted">superAdmin only</span>
                      ) : (
                        <span className="requisitionTableMuted">Approved</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="requisitionEmptyState">
          <h6>No requisitions match this filter</h6>
          <p>Try another status or search term to see more requests.</p>
        </div>
      )}

      <ApprovalActionModal
        show={actionModalShow}
        onHide={() => {
          if (!isSubmittingAction) {
            setActionModalShow(false);
          }
        }}
        onSubmit={handleActionSubmit}
        action={action}
        setAction={setAction}
        comment={comment}
        setComment={setComment}
        selectedRequisition={selectedRequisition}
        isSubmitting={isSubmittingAction}
      />
        </>
      )}
    </div>
  );
}

function ApprovalActionModal({
  show,
  onHide,
  onSubmit,
  action,
  setAction,
  comment,
  setComment,
  selectedRequisition,
  isSubmitting
}) {
  return (
    <Modal show={show} onHide={isSubmitting ? undefined : onHide} centered>
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>Review requisition</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedRequisition ? (
          <div className="requisitionApprovalModalBody">
            <div className="requisitionApprovalPreview">
              <h6>{selectedRequisition.itemTitle}</h6>
              <p>{selectedRequisition.itemDesc}</p>
              <span>{formatRequisitionCurrency(selectedRequisition.totalAmount)}</span>
            </div>

            <div className="requisitionApprovalStateGrid">
              <div className="requisitionApprovalStateCard">
                <span>Approval status</span>
                <strong>{normalizeRequisitionStatus(selectedRequisition.status)}</strong>
              </div>
              <div className="requisitionApprovalStateCard">
                <span>Payment status</span>
                <strong>{selectedRequisition.paymentStatus || (normalizeRequisitionStatus(selectedRequisition.status) === 'Approved' ? 'Pending' : 'Not initiated')}</strong>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Action</Form.Label>
              <Form.Select value={action} onChange={(event) => setAction(event.target.value)}>
                {actionOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {action === 'Approved' ? (
              <div className="requisitionPaymentHint">
                <strong>Lotus payment:</strong> This approval will submit the full requisition amount to Lotus via MPESA.
                <span>Payment will be tracked as pending here until Lotus confirms it.</span>
              </div>
            ) : null}

            <Form.Group className="mb-0">
              <Form.Label>Finance note</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Leave a note explaining the approval decision."
              />
            </Form.Group>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
          Close
        </Button>
        <Button variant="primary" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span> Saving...</span>
            </>
          ) : (
            'Save decision'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RequisitionApprovalsComp;
